import type { ThemeTokens } from "@klarity/themes";
import clsx from "clsx";
import { Ellipsis, PanelRightClose, X } from "lucide-react";
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Menu } from "../../components/ui/Menu";
import { EmptyState, Spinner } from "../../components/ui/misc";
import { Crumb, Page, PageBar } from "../../components/ui/PageBar";
import { ApiError } from "../../lib/api/client";
import { api } from "../../lib/api/endpoints";
import type { DocumentFull } from "../../lib/api/models";
import { can, useMe } from "../../lib/auth";
import { validateMeta } from "../../lib/meta";
import { useDocument, usePublishedTheme, useSpaces } from "../../lib/queries";
import { ThemeCanvas } from "../canvas/ThemeCanvas";
import { EditSession } from "../editing/EditSession";
import { SaveStatus } from "../editing/SaveStatus";
import { useDocumentHost } from "../editing/useDocumentHost";
import { FormatBar } from "../editor/FormatBar";
import { KlarityEditor } from "../editor/KlarityEditor";
import { HistoryPanel } from "../history/HistoryPanel";
import { RevisionView } from "../history/RevisionView";
import { MoveToDialog } from "../tree/MoveToDialog";
import { CoversPicker } from "./CoversPicker";
import { DocumentHeader } from "./DocumentHeader";
import { ExportMenu } from "./ExportMenu";
import { MetaForm } from "./MetaForm";
import { PdfPanel } from "./PdfPanel";

type Tab = "details" | "history";
const TABS: ReadonlyArray<readonly [Tab, string]> = [
  ["details", "Details"],
  ["history", "History"],
];
const CLOSE =
  "grid h-7.5 w-7.5 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-green-wash hover:text-green-ink";
const FOLDED_KEY = "kd.panel.folded";

/** Whether the panel was folded on the last visit. Without storage it starts open. */
function readFolded(): boolean {
  try {
    return window.localStorage.getItem(FOLDED_KEY) === "1";
  } catch {
    return false;
  }
}

/** The right-hand side of the document page (redesign spec §5.3). */
type Side = {
  tab: Tab;
  onTab: (tab: Tab) => void;
  /** What the open tab shows. */
  body: ReactNode;
  /** The PDF preview. When set it takes the panel's place. */
  preview: ReactNode | null;
  onClosePreview: () => void;
  /** Below 1280 pixels the panel is closed until asked for; from 1280 pixels it is open unless folded. */
  open: boolean;
  onClose: () => void;
  /** From 1280 pixels the panel can be folded away to give the document the width; the drawer ignores it. */
  folded: boolean;
  onFold: () => void;
  foldButton: RefObject<HTMLButtonElement | null>;
};

function SidePanel({ tab, onTab, body, preview, onClosePreview, open, onClose, folded, onFold, foldButton }: Side) {
  if (preview) {
    return (
      <aside
        key="preview"
        aria-label="Preview PDF"
        className="flex w-[min(46vw,620px)] min-w-80 flex-col gap-3 border-l border-line bg-surface p-4 max-md:fixed max-md:inset-0 max-md:z-30 max-md:w-auto max-md:min-w-0"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-ink">Preview PDF</h2>
          <button type="button" aria-label="Close preview" onClick={onClosePreview} className={CLOSE}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">{preview}</div>
      </aside>
    );
  }
  return (
    <aside
      key="panel"
      aria-label="Document panel"
      className={clsx(
        "w-75 max-w-full shrink-0 flex-col gap-4 overflow-y-auto border-l border-line bg-surface px-5 pt-4 pb-6",
        open ? "fixed inset-y-0 right-0 z-30 flex shadow-pop" : "hidden",
        folded ? "xl:hidden" : "xl:static xl:flex xl:shadow-none",
      )}
    >
      <div className="flex items-center gap-2">
        <div role="tablist" aria-label="Document panel" className="flex flex-1 gap-1 rounded-lg bg-desk p-1">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => onTab(key)}
              className={clsx(
                "h-7.5 flex-1 rounded-md text-sm",
                tab === key ? "bg-surface font-semibold text-ink shadow-sm" : "text-ink-2 hover:text-ink",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <button type="button" aria-label="Close panel" onClick={onClose} className={clsx(CLOSE, "xl:hidden")}>
          <X size={16} aria-hidden="true" />
        </button>
        <button
          ref={foldButton}
          type="button"
          aria-label="Fold panel"
          onClick={onFold}
          className={clsx(CLOSE, "max-xl:hidden!")}
        >
          <PanelRightClose size={16} aria-hidden="true" />
        </button>
      </div>
      <div role="tabpanel" aria-label={tab === "details" ? "Details" : "History"}>
        {body}
      </div>
    </aside>
  );
}

function Layout({
  header,
  notices,
  toolbar,
  main,
  side,
}: {
  header: ReactNode;
  notices?: ReactNode;
  toolbar?: ReactNode;
  main: ReactNode;
  side: Side;
}) {
  return (
    <div className="flex h-full min-h-0">
      <div className="min-w-0 flex-1 overflow-auto">
        {header}
        <div className="grid gap-3 px-4 pt-3 md:px-6">
          {notices}
          {toolbar ? <div className="sticky top-[86px] z-10 flex justify-center">{toolbar}</div> : null}
        </div>
        <div className="px-4 pt-5 pb-12 md:px-6">{main}</div>
      </div>
      <SidePanel {...side} />
    </div>
  );
}

function Loaded({
  doc,
  tokens,
  startEditing,
  onEditingChange,
}: {
  doc: DocumentFull;
  tokens: ThemeTokens;
  startEditing: boolean;
  onEditingChange: (editing: boolean) => void;
}) {
  const me = useMe();
  const { data: spaces } = useSpaces();
  const clientId = spaces?.find((s) => s.id === doc.spaceId)?.kind === "client" ? doc.spaceId : null;
  // The server refuses edits to archived documents, and to templates for non-admins (auth/permissions.ts).
  const editable = can(me, "edit") && !doc.archivedAt && (doc.kind !== "template" || me?.role === "admin");
  const [editing, setEditing] = useState(startEditing && editable);
  useEffect(() => onEditingChange(editing), [editing, onEditingChange]);
  const [tab, setTab] = useState<Tab>("details");
  const [previewing, setPreviewing] = useState(false);
  const previewButton = useRef<HTMLButtonElement>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [folded, setFolded] = useState(readFolded);
  const foldButton = useRef<HTMLButtonElement>(null);
  const unfoldButton = useRef<HTMLButtonElement>(null);
  // Folding or unfolding removes the button that was pressed: focus goes to the one that takes its place.
  const focusAfterFold = useRef(false);
  useEffect(() => {
    if (!focusAfterFold.current) return;
    focusAfterFold.current = false;
    (folded ? unfoldButton : foldButton).current?.focus();
  }, [folded]);
  const toggleFolded = () => {
    const next = !folded;
    focusAfterFold.current = true;
    setFolded(next);
    try {
      window.localStorage.setItem(FOLDED_KEY, next ? "1" : "0");
    } catch {
      // No storage (a private window): the choice lives for this visit only.
    }
  };
  const [revision, setRevision] = useState<number | null>(null);
  const [moving, setMoving] = useState(false);
  // To another topic or client (document types spec §4.4). What is in Templates stays there, as in the tree.
  const inTemplates = spaces?.find((s) => s.id === doc.spaceId)?.kind === "templates";
  const movable = can(me, "move") && !doc.archivedAt && doc.kind !== "template" && !inTemplates;
  const { host, dialogs } = useDocumentHost(doc.id, tokens, doc.meta);
  const loadSaved = useCallback(() => api.exports.preview(doc.id), [doc.id]);

  // Leaving the History tab or opening the preview closes the open version.
  const selectTab = (next: Tab) => {
    setTab(next);
    if (next !== "history") setRevision(null);
  };
  const togglePreview = () => {
    setPreviewing((p) => !p);
    setRevision(null);
  };
  const closePreview = () => {
    togglePreview();
    previewButton.current?.focus();
  };
  const side = (
    meta: typeof doc.meta,
    setMeta?: (m: typeof doc.meta) => void,
    loadPreview: () => Promise<Blob> = loadSaved,
  ): Side => ({
    tab,
    onTab: selectTab,
    body:
      tab === "details" ? (
        <>
          <MetaForm
            kind={doc.kind}
            meta={meta}
            onChange={setMeta ?? (() => {})}
            errors={setMeta ? validateMeta(doc.kind, meta) : {}}
            readOnly={!setMeta}
          />
          {clientId ? (
            <CoversPicker docId={doc.id} clientId={clientId} readOnly={!can(me, "edit") || Boolean(doc.archivedAt)} />
          ) : null}
        </>
      ) : (
        <HistoryPanel docId={doc.id} selected={revision} onSelect={setRevision} disabled={editing} />
      ),
    preview: previewing ? <PdfPanel key={doc.version} title={doc.title} load={loadPreview} /> : null,
    onClosePreview: closePreview,
    open: panelOpen,
    onClose: () => setPanelOpen(false),
    folded,
    onFold: toggleFolded,
    foldButton,
  });
  const panelControls = {
    preview: previewing,
    onPreview: togglePreview,
    previewButton,
    panelOpen,
    onPanel: () => setPanelOpen((o) => !o),
    panelFolded: folded,
    onUnfold: toggleFolded,
    unfoldButton,
  };

  if (editing) {
    return (
      <EditSession key={doc.id} doc={doc} tokens={tokens} onDone={() => setEditing(false)}>
        {(s) => (
          <Layout
            header={
              <DocumentHeader
                doc={doc}
                meId={me?.id}
                title={s.title}
                editing
                onTitleChange={s.editable ? s.setTitle : undefined}
                status={<SaveStatus status={s.status} lastSavedAt={s.lastSavedAt} error={s.error} />}
                {...panelControls}
                canEdit
                onEdit={() => {}}
                onDone={() => void s.done()}
                exportMenu={
                  <ExportMenu
                    docId={doc.id}
                    getContent={() => s.snapshot().content}
                    beforeExport={() => s.flush()}
                    unsaved={s.pending}
                  />
                }
              />
            }
            notices={s.notices}
            toolbar={s.editor ? <FormatBar editor={s.editor} /> : null}
            main={s.canvas}
            side={side(s.meta, s.editable ? s.setMeta : undefined, () => api.exports.preview(doc.id, s.snapshot()))}
          />
        )}
      </EditSession>
    );
  }

  return (
    <>
      {moving ? <MoveToDialog doc={doc} open onClose={() => setMoving(false)} /> : null}
      <Layout
        header={
          <DocumentHeader
            doc={doc}
            meId={me?.id}
            title={doc.title}
            editing={false}
            {...panelControls}
            canEdit={editable}
            onEdit={() => {
              setRevision(null);
              setEditing(true);
            }}
            onDone={() => {}}
            exportMenu={<ExportMenu docId={doc.id} getContent={() => doc.content} />}
            moreMenu={
              movable ? (
                <Menu
                  label="More"
                  align="end"
                  trigger={
                    <Button aria-label="More" className="w-9 px-0!">
                      <Ellipsis size={16} aria-hidden="true" />
                    </Button>
                  }
                  items={[{ id: "moveto", label: "Move to…", onSelect: () => setMoving(true) }]}
                />
              ) : null
            }
          />
        }
        main={
          revision !== null ? (
            <RevisionView doc={doc} tokens={tokens} version={revision} onClose={() => setRevision(null)} />
          ) : (
            <ThemeCanvas
              tokens={tokens}
              fileUrl={host.attachmentUrl}
              doc={{ kind: doc.kind, title: doc.title, meta: doc.meta }}
            >
              <KlarityEditor key={doc.version} content={doc.content} mode="read" host={host} />
              {dialogs}
            </ThemeCanvas>
          )
        }
        side={side(doc.meta)}
      />
    </>
  );
}

/** A document that cannot be shown still gets a page bar, so the menu and search stay within reach. */
function Unavailable({ children }: { children: ReactNode }) {
  return (
    <>
      <PageBar>
        <Crumb current>Document</Crumb>
      </PageBar>
      <Page>{children}</Page>
    </>
  );
}

export function DocumentPage({ docRef, startEditing = false }: { docRef: string; startEditing?: boolean }) {
  const [editing, setEditing] = useState(false);
  // While editing, returning to the window must not refetch: the session holds the working copy.
  const { data: doc, error, isLoading } = useDocument(docRef, { refetchOnWindowFocus: !editing });
  const { data: theme, error: themeError } = usePublishedTheme(doc?.themeId ?? null, Boolean(doc));
  // A failed background refetch keeps the cached data (TanStack Query v5): only a first load that fails replaces
  // the page, so a network drop never unmounts an open edit session.
  const docError = doc ? null : error;
  const loadError = docError ?? (theme ? null : themeError);
  if (docError instanceof ApiError && docError.status === 403)
    return (
      <Unavailable>
        <EmptyState
          title="You don't have access to this document"
          body="Ask an admin to add you to its topic or client."
        />
      </Unavailable>
    );
  if (docError instanceof ApiError && docError.status === 404)
    return (
      <Unavailable>
        <EmptyState title="This document does not exist or was archived" />
      </Unavailable>
    );
  if (loadError)
    return (
      <Unavailable>
        <EmptyState title="This document could not be loaded" body={loadError.message} />
      </Unavailable>
    );
  if (isLoading || !doc || !theme)
    return (
      <Unavailable>
        <Spinner label="Loading document…" />
      </Unavailable>
    );
  return (
    <Loaded
      key={doc.id}
      doc={doc}
      tokens={theme.data.tokens}
      startEditing={startEditing}
      onEditingChange={setEditing}
    />
  );
}
